import { useEffect, useMemo, useRef, useState } from "react";

export interface WorthNumbersRandomizerOptions {
  // interval between randomizations while processing
  intervalMs?: number;
  // percentage variance used to bound jitter (around a seed baseline)
  percentageVariance?: number; // applied relative to seedYour when available
  // optional seed to bias the random range towards realistic values
  seedYour?: number | null | undefined;
  // optional seed for historical/industry baseline number
  seedHistorical?: number | null | undefined;
}

export interface WorthNumbersRandomizerResult {
  historicalNumber: number | null;
  yourNumber: number | null;
  targetValue: number | null;
  totalMissingValue: number | null;
}

// Generates four synchronized numbers while enabled:
// 1) historicalNumber: random (optionally around seedHistorical)
// 2) yourNumber: random (optionally around seedYour)
// 3) targetValue: random but guaranteed to be >= yourNumber
// 4) totalMissingValue: targetValue - yourNumber
export function useWorthNumbersRandomizer(
  enabled: boolean,
  options?: WorthNumbersRandomizerOptions,
): WorthNumbersRandomizerResult {
  const { intervalMs = 1200, percentageVariance = 20, seedYour, seedHistorical } = options || {};

  const [state, setState] = useState<WorthNumbersRandomizerResult>({
    historicalNumber: null,
    yourNumber: null,
    targetValue: null,
    totalMissingValue: null,
  });

  const timerRef = useRef<number | null>(null);

  const clampedVariance = useMemo(() => {
    return Math.max(0, Math.min(100, Math.abs(percentageVariance)));
  }, [percentageVariance]);

  useEffect(() => {
    // reset when not enabled
    if (!enabled) {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return () => undefined;
    }

    // compute bounds relative to a baseline to keep numbers stable-ish
    const baseline = typeof seedYour === "number" && seedYour > 0 ? seedYour : 100000;
    const varianceAmount = baseline * (clampedVariance / 100);
    const minYour = Math.max(0, baseline - varianceAmount);
    const maxYour = baseline + varianceAmount;

    // compute bounds for historical number
    const historicalBaseline = typeof seedHistorical === "number" && seedHistorical > 0 ? seedHistorical : 100000;
    const historicalVarianceAmount = historicalBaseline * (clampedVariance / 100);
    const minHistorical = Math.max(0, historicalBaseline - historicalVarianceAmount);
    const maxHistorical = historicalBaseline + historicalVarianceAmount;

    const tick = () => {
      const nextHistorical = Math.round(minHistorical + Math.random() * (maxHistorical - minHistorical));
      const nextYour = Math.round(minYour + Math.random() * (maxYour - minYour));

      // For target, ensure >= your. Let target have its own variance band, but floor at your.
      const targetBaseline = Math.max(nextYour, baseline);
      const targetVarianceAmount = targetBaseline * (clampedVariance / 100);
      const minTarget = Math.max(nextYour, targetBaseline); // at least your and baseline
      const maxTarget = targetBaseline + targetVarianceAmount;
      const nextTarget = Math.round(minTarget + Math.random() * (maxTarget - minTarget));

      setState({
        historicalNumber: nextHistorical,
        yourNumber: nextYour,
        targetValue: nextTarget,
        totalMissingValue: nextTarget - nextYour,
      });
    };

    // initial tick immediately for snappy UI
    tick();
    timerRef.current = window.setInterval(tick, intervalMs);

    return () => {
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, intervalMs, clampedVariance, seedYour, seedHistorical]);

  return state;
}

export default useWorthNumbersRandomizer;
